import { appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
// MessageConfiguration.js
//
// "Message configuration" view — a normal main-area route (currentView
// "message_config"), opened from the left rail beneath "Single pharmacy". Left panel: the branches the signed-in org can see.
// Right panel: that branch's shelf messages (tenant DB, isDeleted:false) —
// title, content and the includeLocation flag — each with an Edit button that
// lets the manager change the text and toggle location, then Save / Cancel.
// The list is re-fetched from the server after every save.
//
// Backend: shelf_messages_routes.js
//   GET  /api/shelf-messages/branches
//   GET  /api/shelf-messages?dbName=
//   POST /api/shelf-messages/update { dbName, id, shelfMessage, includeLocation }

import React, { useCallback, useEffect, useState } from "react";
import { useMaskNames, displayName } from "./nameMask";
const API_BASE = apiUrl("");
async function api(path, token, opts = {}) {
  const res = await fetch(`${API_BASE}/api/shelf-messages${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {})
    }
  });
  let body = null;
  try {
    body = await res.json();
  } catch (_) {}
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}
const fmtDate = d => {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt) ? "" : dt.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};
export default function MessageConfiguration({
  token
}) {
  const masked = useMaskNames();
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [branchesError, setBranchesError] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [draftLocation, setDraftLocation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Esc cancels an open edit.
  useEffect(() => {
    if (!editingId) return undefined;
    const onKey = e => {
      if (e.key === "Escape") {
        setEditingId(null);
        setSaveError("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingId]);
  useEffect(() => {
    let cancelled = false;
    setBranchesLoading(true);
    api("/branches", token).then(list => {
      if (!cancelled) setBranches(Array.isArray(list) ? list : []);
    }).catch(e => {
      if (!cancelled) setBranchesError(e.message);
    }).finally(() => {
      if (!cancelled) setBranchesLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const loadMessages = useCallback(async branch => {
    if (!branch) return;
    setMsgLoading(true);
    setMsgError("");
    try {
      const r = await api(`?dbName=${encodeURIComponent(branch.dbName)}`, token);
      setMessages(r?.messages || []);
    } catch (e) {
      setMessages([]);
      setMsgError(e.message);
    } finally {
      setMsgLoading(false);
    }
  }, [token]);
  const pickBranch = async b => {
    if (editingId && !(await appConfirm("Discard the unsaved edit?"))) return;
    setEditingId(null);
    setSaveError("");
    setSelected(b);
    setMessages([]);
    loadMessages(b);
  };
  const startEdit = m => {
    setEditingId(m._id);
    setDraftText(m.shelfMessage);
    setDraftLocation(!!m.includeLocation);
    setSaveError("");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setSaveError("");
  };
  const save = async () => {
    if (!draftText.trim()) {
      setSaveError("Message text cannot be empty.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await api("/update", token, {
        method: "POST",
        body: JSON.stringify({
          dbName: selected.dbName,
          id: editingId,
          shelfMessage: draftText,
          includeLocation: draftLocation
        })
      });
      setEditingId(null);
      await loadMessages(selected); // refresh from the server
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };
  const shownBranches = branches.filter(b => !branchFilter.trim() || (b.pharmacyName || "").toLowerCase().includes(branchFilter.trim().toLowerCase()));
  return <div style={S.page}>
      <div style={S.header}>
        <div style={S.title}>Message configuration</div>
        <div style={S.subtitle}>Shelf messages sent to patients, per branch. Select a branch to view and edit its messages.</div>
      </div>

      <div style={S.body}>
        {/* ---------- Left: branches ---------- */}
        <div style={S.left}>
          <input value={branchFilter} onChange={e => setBranchFilter(e.target.value)} placeholder="Filter branches…" style={S.filter} />
          <div style={S.branchList}>
            {branchesLoading && <div style={S.muted}>Loading branches…</div>}
            {branchesError && <div style={S.error}>{branchesError}</div>}
            {!branchesLoading && !branchesError && shownBranches.length === 0 && <div style={S.muted}>No branches found.</div>}
            {shownBranches.map(b => {
            const on = selected?.dbName === b.dbName;
            return <button key={b.dbName} onClick={() => pickBranch(b)} style={{
              ...S.branchBtn,
              ...(on ? S.branchBtnOn : null)
            }}>
                  {displayName(b.pharmacyName, masked)}
                </button>;
          })}
          </div>
        </div>

        {/* ---------- Right: messages ---------- */}
        <div style={S.right}>
          {!selected && <div style={S.placeholder}>← Choose a branch to see its messages.</div>}

          {selected && <>
              <div style={S.rightHead}>
                <div style={S.branchTitle}>{displayName(selected.pharmacyName, masked)}</div>
                <button onClick={() => loadMessages(selected)} style={S.secondaryBtn} disabled={msgLoading || !!editingId}>Refresh</button>
              </div>

              {msgLoading && <div style={S.muted}>Loading messages…</div>}
              {msgError && <div style={S.error}>{msgError}</div>}
              {!msgLoading && !msgError && messages.length === 0 && <div style={S.muted}>This branch has no shelf messages.</div>}

              {messages.length > 0 && <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={{
                  ...S.th,
                  width: "22%"
                }}>Title</th>
                      <th style={S.th}>Content</th>
                      <th style={{
                  ...S.th,
                  width: 90,
                  textAlign: "center"
                }}>Location</th>
                      <th style={{
                  ...S.th,
                  width: 170
                }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map(m => {
                const editing = editingId === m._id;
                return <tr key={m._id} style={editing ? S.rowEditing : null}>
                          <td style={S.td}>
                            <div style={{
                      fontWeight: 600
                    }}>{m.messageTitle || <em style={S.muted}>(untitled)</em>}</div>
                            {m.isDefault && <span style={S.badge}>Default</span>}
                            {m.updatedAt && <div style={S.small}>Updated {fmtDate(m.updatedAt)}</div>}
                          </td>
                          <td style={S.td}>
                            {editing ? <textarea value={draftText} onChange={e => setDraftText(e.target.value)} rows={3} maxLength={1000} autoFocus style={S.textarea} /> : <div style={{
                      whiteSpace: "pre-wrap"
                    }}>{m.shelfMessage}</div>}
                            {editing && saveError && <div style={S.error}>{saveError}</div>}
                          </td>
                          <td style={{
                    ...S.td,
                    textAlign: "center"
                  }}>
                            {editing ? <input type="checkbox" checked={draftLocation} onChange={e => setDraftLocation(e.target.checked)} style={{
                      width: 18,
                      height: 18,
                      cursor: "pointer"
                    }} title="Include the shelf location in the message" /> : <span style={m.includeLocation ? S.yes : S.no}>
                                {m.includeLocation ? "✓ Yes" : "No"}
                              </span>}
                          </td>
                          <td style={{
                    ...S.td,
                    textAlign: "right",
                    whiteSpace: "nowrap"
                  }}>
                            {editing ? <>
                                <button onClick={cancelEdit} style={S.secondaryBtn} disabled={saving}>Cancel</button>{" "}
                                <button onClick={save} style={S.primaryBtn} disabled={saving}>
                                  {saving ? "Saving…" : "Save"}
                                </button>
                              </> : <button onClick={() => startEdit(m)} style={S.secondaryBtn} disabled={!!editingId}>Edit</button>}
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>}
            </>}
        </div>
      </div>
    </div>;
}
const TEAL = "#0E8CFF";
const S = {
  page: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    padding: "12px 16px",
    boxSizing: "border-box"
  },
  header: {
    marginBottom: 12
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#0958D9"
  },
  subtitle: {
    fontSize: 13,
    color: "#5D5D5D",
    marginTop: 2
  },
  body: {
    display: "flex",
    height: "calc(100vh - 140px)",
    minHeight: 420,
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    overflow: "hidden",
    background: "#FAFAFA"
  },
  left: {
    width: 300,
    flexShrink: 0,
    borderRight: "1px solid #F0F0F0",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    minHeight: 0
  },
  filter: {
    margin: 12,
    padding: "8px 10px",
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontSize: 14
  },
  branchList: {
    flex: 1,
    overflowY: "auto",
    padding: "0 8px 12px"
  },
  branchBtn: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "9px 10px",
    marginBottom: 4,
    border: "1px solid transparent",
    borderRadius: 6,
    background: "transparent",
    cursor: "pointer",
    fontSize: 14,
    color: "#262626"
  },
  branchBtnOn: {
    background: "#E6F7FF",
    borderColor: TEAL,
    borderLeft: `4px solid ${TEAL}`,
    paddingLeft: 7,
    fontWeight: 700,
    color: "#0958D9"
  },
  right: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 24px",
    minWidth: 0
  },
  rightHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12
  },
  branchTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#262626"
  },
  placeholder: {
    color: "#5D5D5D",
    fontSize: 15,
    marginTop: 40,
    textAlign: "center"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 8,
    overflow: "hidden"
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    background: "#F5F5F5",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#5D5D5D",
    borderBottom: "1px solid #F0F0F0"
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid #F5F5F5",
    verticalAlign: "top",
    fontSize: 14
  },
  rowEditing: {
    background: "#E6F7FF"
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: 8,
    fontSize: 14,
    fontFamily: "inherit",
    border: `1px solid ${TEAL}`,
    borderRadius: 6,
    resize: "vertical"
  },
  badge: {
    display: "inline-block",
    marginTop: 4,
    padding: "1px 7px",
    fontSize: 11,
    borderRadius: 10,
    background: "#E6F7FF",
    color: "#0958D9",
    fontWeight: 600
  },
  small: {
    fontSize: 11,
    color: "#8C8C8C",
    marginTop: 4
  },
  yes: {
    color: "#15803d",
    fontWeight: 600
  },
  no: {
    color: "#5D5D5D"
  },
  primaryBtn: {
    padding: "6px 14px",
    border: `1px solid ${TEAL}`,
    borderRadius: 6,
    background: TEAL,
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600
  },
  secondaryBtn: {
    padding: "6px 14px",
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    background: "#fff",
    color: "#262626",
    cursor: "pointer",
    fontWeight: 600
  },
  muted: {
    color: "#5D5D5D",
    fontSize: 14,
    padding: "8px 2px"
  },
  error: {
    color: "#CF1322",
    fontSize: 13,
    padding: "6px 2px"
  }
};
